"use client";

import * as React from "react";
import {
  useWalletPortfolioContext,
  usePortfolioFiat,
} from "../../providers/wallet-portfolio-provider";
import { useGameSwapPools } from "../../queries/use-game-swap-pools";
import { useGameSwapLpBalances } from "../../queries/use-game-swap-lp-balances";
import { APP_LAUNCHER_ICONS_BASE, GAME_SWAP_WEB } from "../../constants";
import type { GameSwapPool, LpBalanceInfo } from "../../types";
import {
  formatFiatFromUsd,
  formatPortfolioTokenAmount,
  type PortfolioFiat,
} from "../../utils";
import { SectionCard } from "./SectionCard";
import { ExternalLinkIcon } from "../icons";
import { handleOutlinkClick } from "../../../../lib/outlink";
import BigNumber from "bignumber.js";

const SECTION_TITLE = "Gametoken LP";
const DEFAULT_DECIMALS = 18;
/** game-swap FE와 동일하게 LP 0.0001 미만 포지션은 dust로 숨긴다. */
const LP_DUST_THRESHOLD = new BigNumber("0.0001");

function isCross(symbol: string | undefined): boolean {
  return symbol?.trim().toUpperCase() === "CROSS";
}

/**
 * 백엔드가 CROSS 쪽 token ref를 빈 값(symbol "", decimals 0)으로 내려주므로,
 * 0/미지정 decimals는 unset으로 보고 18(CROSS native)로 간주한다.
 */
function tokenDecimals(decimals: number | undefined): number {
  return decimals && decimals > 0 ? decimals : DEFAULT_DECIMALS;
}

/** 풀의 CROSS 쪽 예비량을 기준으로 한 총 유동성 가치(CROSS) = 2 × crossReserve. */
function poolCrossValue(pool: GameSwapPool): BigNumber {
  const crossOnA = isCross(pool.token_a?.symbol) && !isCross(pool.token_b?.symbol);
  const reserve = crossOnA ? pool.reserve_a : pool.reserve_b;
  const decimals = tokenDecimals(
    crossOnA ? pool.token_a?.decimals : pool.token_b?.decimals,
  );
  const crossAmount = new BigNumber(reserve || "0").shiftedBy(-decimals);
  if (!crossAmount.isFinite() || crossAmount.isLessThanOrEqualTo(0)) {
    return new BigNumber(0);
  }
  return crossAmount.times(2);
}

interface PoolRow {
  pool: GameSwapPool;
  tokenASymbol: string;
  tokenBSymbol: string;
  info?: LpBalanceInfo;
  lpAmountRaw?: string;
  lpDecimals: number;
  sharePercent?: BigNumber;
  valueText?: string;
}

function buildRow(
  pool: GameSwapPool,
  info: LpBalanceInfo | undefined,
  fiat: PortfolioFiat,
): PoolRow {
  const tokenASymbol = pool.token_a?.symbol?.trim() || "TOKEN";
  const tokenBSymbol = pool.token_b?.symbol?.trim() || "CROSS";
  const lpDecimals = info?.decimals ?? DEFAULT_DECIMALS;

  let sharePercent: BigNumber | undefined;
  let valueText: string | undefined;

  if (info && info.totalSupply && info.totalSupply > BigInt(0)) {
    const balance = new BigNumber(info.balance.toString());
    const totalSupply = new BigNumber(info.totalSupply.toString());
    const ratio = balance.div(totalSupply);
    sharePercent = ratio.times(100);

    const positionCross = ratio.times(poolCrossValue(pool));
    if (positionCross.isGreaterThan(0)) {
      const crossUsd = new BigNumber(pool.quote_usd ?? pool.cross_usd ?? "0");
      valueText = crossUsd.isGreaterThan(0)
        ? formatFiatFromUsd(positionCross.times(crossUsd), fiat)
        : `${positionCross.toFormat(4, BigNumber.ROUND_DOWN)} CROSS`;
    }
  }

  return {
    pool,
    tokenASymbol,
    tokenBSymbol,
    info,
    lpAmountRaw: info?.balance.toString(),
    lpDecimals,
    sharePercent,
    valueText,
  };
}

export function GameSwapSection() {
  const { env, walletAddress, onOutlink, lpBalanceReader } =
    useWalletPortfolioContext();
  const fiat = usePortfolioFiat();
  const hasAddress = !!walletAddress;
  const { pools, isLoading: poolsLoading } = useGameSwapPools(
    env,
    walletAddress,
    hasAddress,
  );

  const pairAddresses = React.useMemo(
    () => pools.map((p) => p.pair_address).filter(Boolean),
    [pools],
  );
  const { balances, isLoading: balancesLoading } = useGameSwapLpBalances(
    env,
    walletAddress,
    pairAddresses,
    lpBalanceReader,
  );

  // 전용 game-swap 아이콘이 버킷에 확인되지 않아, 존재가 검증된 dex.png를 사용한다.
  // 전용 에셋이 추가되면 파일명만 교체하면 된다.
  const sectionIcon = `${APP_LAUNCHER_ICONS_BASE[env]}/dex.png`;
  // 주입형 reader 또는 내부 RPC 폴백으로 잔고가 실제 조회됐는지 여부.
  // 둘 다 실패하면 빈 맵이라 dust 필터링 없이 풀 정보만 표시한다.
  const hasBalances = Object.keys(balances).length > 0;

  const rows = React.useMemo(() => {
    const built = pools.map((pool) =>
      buildRow(pool, balances[pool.pair_address?.toLowerCase()], fiat),
    );
    // 온체인 잔고를 알 때만 잔고 기준 dust 필터링이 가능하다.
    if (!hasBalances) return built;
    return built.filter((row) => {
      if (!row.lpAmountRaw) return false;
      const amount = new BigNumber(row.lpAmountRaw).shiftedBy(-row.lpDecimals);
      return amount.isGreaterThanOrEqualTo(LP_DUST_THRESHOLD);
    });
  }, [pools, balances, hasBalances, fiat]);

  const getManageUrl = (pool: GameSwapPool) => {
    const tokenAddress = pool.token_a?.address ?? pool.pair_address;
    return `${GAME_SWAP_WEB[env]}/tokens/${tokenAddress}?trade=deposit`;
  };

  return (
    <SectionCard
      sectionKey="gameSwap"
      title={SECTION_TITLE}
      icon={sectionIcon}
      iconFit="contain"
      isEmpty={rows.length === 0}
      loading={poolsLoading || balancesLoading}
    >
      {rows.map((row) => {
        const { pool } = row;
        const manageUrl = getManageUrl(pool);
        const pairLabel = `${row.tokenASymbol} / ${row.tokenBSymbol}`;
        const tokenAIcon = pool.token_a?.logo_url;
        const tokenBIcon = pool.token_b?.logo_url;

        return (
          <article className="wp-item" data-side="game-swap" key={pool.pair_address}>
            <div className="wp-item-body">
              <div className="wp-item-line">
                <span className="wp-item-label">Pool</span>
                <div className="wp-svc-token">
                  <span className="wp-token-stack" aria-hidden>
                    {tokenAIcon && (
                      <img className="wp-svc-token-icon" src={tokenAIcon} alt="" />
                    )}
                    {tokenBIcon && (
                      <img className="wp-svc-token-icon" src={tokenBIcon} alt="" />
                    )}
                  </span>
                  <span>{pairLabel}</span>
                </div>
              </div>
              {row.lpAmountRaw !== undefined && (
                <div className="wp-item-line">
                  <span className="wp-item-label">My LP</span>
                  <span>
                    {formatPortfolioTokenAmount(row.lpAmountRaw, row.lpDecimals)} LP
                  </span>
                </div>
              )}
              {row.sharePercent !== undefined && (
                <div className="wp-item-line">
                  <span className="wp-item-label">Pool Share</span>
                  <span>
                    {row.sharePercent.toFormat(2, BigNumber.ROUND_DOWN)}%
                  </span>
                </div>
              )}
              {row.valueText !== undefined && (
                <div className="wp-item-line">
                  <span className="wp-item-label">Value</span>
                  <span>{row.valueText}</span>
                </div>
              )}
            </div>
            <div className="wp-item-tags">
              <a
                className="wp-tag wp-tag--mint"
                href={manageUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-track="gameswap_outlink"
                data-track-id={pairLabel}
                onClick={(e) =>
                  handleOutlinkClick(
                    e,
                    manageUrl,
                    {
                      category: "portfolio",
                      origin: "portfolio-game-swap",
                      payload: { pool },
                    },
                    onOutlink,
                  )
                }
              >
                <span className="wp-tag-label">Manage</span>
                <ExternalLinkIcon size={12} className="wp-tag-icon" />
              </a>
            </div>
          </article>
        );
      })}
    </SectionCard>
  );
}
